import React, { useEffect, useState } from 'react';
import MemoryIcon from '@mui/icons-material/Memory';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ReplayIcon from '@mui/icons-material/Replay';
import TimerIcon from '@mui/icons-material/Timer';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RepeatIcon from '@mui/icons-material/Repeat';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { raffleApi } from './raffleApi';
import WinnersControl from './WinnersControl';
import { SeriesSheet } from './DrawFlow';
import {
  GroupBar,
  MoneyInput,
  Notice,
  SectionLabel,
  Sheet,
  SheetTitle,
  Stepper,
  TicketStub,
  Toggle,
} from './RafflePieces';
import {
  DEFAULT_AUTO_FROM,
  INDIGO,
  NAVY,
  POT_INCREASE_PRESETS,
  SLATE,
  TICKET_COLORS,
  digitsOnly,
  pad,
} from './raffleTokens';

/**
 * Settings — spec §4. Manager only (the gear is hidden for employees and the
 * server refuses every write from them). Both raffle types live together on
 * this one screen, split by section headers. Every change is audited server-side.
 */
export default function SettingsView({
  boot,
  locationId,
  locationName,
  notify,
  reloadBoot,
  onDone,
  registerSave,
}) {
  const [draft, setDraft] = useState(() => ({
    ...boot.settings,
    totalMachines: String(boot.settings.totalMachines || ''),
  }));
  const [oosInput, setOosInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [openDrawing, setOpenDrawing] = useState(null);
  const [newDrawingName, setNewDrawingName] = useState(null);
  // R88 — where a new drawing's numbers come from. Books by default, because
  // that is what every existing drawing is.
  const [newSource, setNewSource] = useState('books');
  const [newAutoFrom, setNewAutoFrom] = useState(String(DEFAULT_AUTO_FROM));
  const [confirmDelete, setConfirmDelete] = useState(null);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const addOos = () => {
    const n = Number(oosInput);
    setOosInput('');
    if (!n || draft.outOfService.includes(n)) return;
    set({ outOfService: [...draft.outOfService, n].sort((a, b) => a - b) });
  };

  const saveSettings = async () => {
    setSaving(true);
    const res = await raffleApi.saveSettings({ locationId, ...draft });
    setSaving(false);
    if (!res.success) return notify('error', res.message);
    notify('success', 'Raffle settings saved.');
    await reloadBoot();
    onDone();
  };

  // The toolbar's Save is the same action as the one at the foot of the page,
  // so the parent is handed this one rather than given a copy that could
  // drift out of step with the draft. Re-registered whenever the draft or the
  // busy flag changes, so the toolbar always calls a handler that can see
  // them.
  useEffect(() => {
    if (registerSave) registerSave({ save: saveSettings, saving });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, saving, registerSave]);

  // Cleared only on the way out, so the toolbar's button never blinks out
  // mid-edit.
  useEffect(
    () => () => {
      if (registerSave) registerSave(null);
    },
    [registerSave]
  );

  const addDrawing = async () => {
    const res = await raffleApi.saveDrawing({
      locationId,
      name: newDrawingName,
      source: newSource,
      autoFrom: newAutoFrom,
    });
    if (!res.success) return notify('error', res.message);
    setNewDrawingName(null);
    setNewSource('books');
    setNewAutoFrom(String(DEFAULT_AUTO_FROM));
    setOpenDrawing(res.drawing.drawingId);
    reloadBoot();
  };

  const deleteDrawing = async () => {
    const res = await raffleApi.deleteDrawing({
      locationId,
      drawingId: confirmDelete.drawingId,
    });
    setConfirmDelete(null);
    if (!res.success) return notify('error', res.message);
    notify('success', 'Drawing removed. Its past raffles stay in History.');
    reloadBoot();
  };

  return (
    // Phones: one column. Wide screens: machine + shared settings on the
    // left, drawings + Winners Control on the right (.rf-settings-grid).
    <div className="rf-pad rf-settings-grid">
      <div className="rf-set-machine rf-col rf-gap-20">
        <GroupBar Icon={MemoryIcon}>Machine number raffle</GroupBar>

        <div>
          <SectionLabel>Machines in play</SectionLabel>
          <div className="rf-card rf-row rf-between rf-gap-12">
            <div>
              <div className="rf-strong">How many machines</div>
              <div className="rf-xs rf-muted">
                The draw picks from 1 to this number
              </div>
            </div>
            <input
              className="rf-input rf-input-num"
              inputMode="numeric"
              value={draft.totalMachines}
              onChange={(e) =>
                set({ totalMachines: digitsOnly(e.target.value, 4) })
              }
            />
          </div>
        </div>

        <div>
          <SectionLabel>Machines out of service</SectionLabel>
          <div className="rf-card">
            <div
              className="rf-row rf-between rf-gap-12"
              style={{ marginBottom: 12 }}
            >
              <div>
                <div className="rf-strong">Skip these machines</div>
                <div className="rf-xs rf-muted">
                  They never come out in the draw
                </div>
              </div>
              <Toggle
                on={draft.skipOutOfService}
                onClick={() =>
                  set({ skipOutOfService: !draft.skipOutOfService })
                }
              />
            </div>
            {/* R7 — off keeps the list; it just stops applying. */}
            <div style={{ opacity: draft.skipOutOfService ? 1 : 0.5 }}>
              <div
                className="rf-row rf-wrap rf-gap-8"
                style={{ marginBottom: 12 }}
              >
                {draft.outOfService.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="rf-oos-chip"
                    onClick={() =>
                      set({
                        outOfService: draft.outOfService.filter((x) => x !== n),
                      })
                    }
                  >
                    {pad(n)} <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                ))}
                {draft.outOfService.length === 0 && (
                  <span className="rf-xs rf-muted">
                    No machines excluded right now.
                  </span>
                )}
              </div>
              <div className="rf-row rf-gap-8">
                <input
                  className="rf-input rf-flex1"
                  inputMode="numeric"
                  placeholder="Machine number"
                  value={oosInput}
                  onChange={(e) => setOosInput(digitsOnly(e.target.value, 4))}
                  onKeyDown={(e) => e.key === 'Enter' && addOos()}
                />
                <button type="button" className="rf-pill-btn" onClick={addOos}>
                  <AddIcon style={{ fontSize: 16 }} /> Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rf-set-tickets rf-col rf-gap-20">
        <GroupBar Icon={ConfirmationNumberIcon}>Ticket number raffle</GroupBar>

        <div>
          <SectionLabel>Drawings</SectionLabel>
          <div className="rf-col rf-gap-12">
            {boot.drawings.map((d) => (
              <DrawingCard
                key={d.drawingId}
                drawing={d}
                open={openDrawing === d.drawingId}
                onToggle={() =>
                  setOpenDrawing(
                    openDrawing === d.drawingId ? null : d.drawingId
                  )
                }
                locationId={locationId}
                notify={notify}
                reloadBoot={reloadBoot}
                onDelete={() => setConfirmDelete(d)}
              />
            ))}

            {newDrawingName !== null ? (
              <div className="rf-card" style={{ borderColor: '#8b90f0' }}>
                <SectionLabel>Drawing name</SectionLabel>
                <input
                  autoFocus
                  className="rf-input"
                  placeholder="Daily Drawing"
                  value={newDrawingName}
                  maxLength={60}
                  onChange={(e) => setNewDrawingName(e.target.value)}
                  style={{ marginBottom: 16 }}
                />

                {/* R88 — books or auto numbers. Fixed once the drawing
                    exists: switching a running cycle onto paper (or back)
                    would strand every number already issued, so a different
                    source is a different drawing. */}
                <SectionLabel>Where do its numbers come from?</SectionLabel>
                <div className="rf-col rf-gap-8" style={{ marginBottom: 16 }}>
                  {[
                    {
                      key: 'books',
                      Icon: ConfirmationNumberIcon,
                      title: 'Physical ticket books',
                      sub: 'Staff hand out printed tickets and record how far each book has gone.',
                    },
                    {
                      key: 'auto',
                      Icon: AutoAwesomeIcon,
                      title: 'NuVe auto numbers',
                      sub: 'Every qualifying visit earns the customer one number automatically. Nothing to hand out, and the winner is known the moment the number comes up.',
                    },
                  ].map(({ key, Icon, title, sub }) => (
                    <button
                      key={key}
                      type="button"
                      className="rf-source-option"
                      data-on={newSource === key ? '1' : '0'}
                      onClick={() => setNewSource(key)}
                    >
                      <Icon
                        style={{
                          fontSize: 20,
                          color: newSource === key ? INDIGO : SLATE,
                          flexShrink: 0,
                        }}
                      />
                      <span className="rf-flex1 rf-minw0 rf-text-left">
                        <span className="rf-strong rf-block">{title}</span>
                        <span className="rf-xs rf-muted">{sub}</span>
                      </span>
                    </button>
                  ))}
                </div>

                {newSource === 'auto' && (
                  <>
                    <SectionLabel>Start numbering at</SectionLabel>
                    <input
                      className="rf-input"
                      inputMode="numeric"
                      value={newAutoFrom}
                      onChange={(e) =>
                        setNewAutoFrom(digitsOnly(e.target.value))
                      }
                      style={{ marginBottom: 16 }}
                    />
                  </>
                )}

                <div className="rf-row rf-gap-8">
                  <button
                    type="button"
                    className="rf-btn rf-btn-outline rf-flex1"
                    onClick={() => setNewDrawingName(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rf-btn rf-btn-navy rf-flex1"
                    disabled={!newDrawingName.trim()}
                    onClick={addDrawing}
                  >
                    Add drawing
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="rf-dashed-btn"
                onClick={() => setNewDrawingName('')}
              >
                <AddIcon fontSize="small" /> Add drawing
              </button>
            )}
          </div>
        </div>

        <GroupBar Icon={EmojiEventsIcon}>Winners control</GroupBar>
        <WinnersControl
          locationId={locationId}
          locationName={locationName}
          notify={notify}
        />
      </div>

      <div className="rf-set-shared rf-col rf-gap-20">
        <GroupBar Icon={ReplayIcon}>Both raffle types</GroupBar>

        <div>
          <SectionLabel>Repeat rule</SectionLabel>
          <div className="rf-card rf-row rf-between rf-gap-12">
            <div>
              <div className="rf-strong">No repeated winning number</div>
              <div className="rf-xs rf-muted">
                A number that already came out in this raffle can't come out
                again, not even on a re-draw. The next raffle starts with the
                full pool.
              </div>
            </div>
            <Toggle
              on={draft.noRepeat}
              onClick={() => set({ noRepeat: !draft.noRepeat })}
            />
          </div>
        </div>

        <div>
          <SectionLabel>Draw animation</SectionLabel>
          <div className="rf-card">
            <div className="rf-row rf-gap-8" style={{ marginBottom: 12 }}>
              <TimerIcon style={{ color: INDIGO, fontSize: 18 }} />
              <div>
                <div className="rf-strong">How long the wheel spins</div>
                <div className="rf-xs rf-muted">
                  Longer spin builds more suspense on the floor
                </div>
              </div>
            </div>
            <div className="rf-row rf-gap-8">
              {(boot.spinOptions || [5, 8, 12]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="rf-seg rf-flex1"
                  data-on={draft.spinSecs === s ? '1' : '0'}
                  onClick={() => set({ spinSecs: s })}
                >
                  {s} sec
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Its own full-width row under both columns: on a wide screen it used
          to sit inside the left column and read as though it only saved what
          was above it. */}
      <div className="rf-set-save rf-col rf-gap-12">
        <button
          type="button"
          className="rf-btn rf-btn-navy rf-w100"
          disabled={saving}
          onClick={saveSettings}
        >
          Save settings
        </button>
        <div className="rf-xs rf-muted rf-text-center">
          Drawings and ticket books save as you change them.
        </div>
      </div>

      {confirmDelete && (
        <Sheet onClose={() => setConfirmDelete(null)}>
          <SheetTitle
            Icon={DeleteOutlineIcon}
            onClose={() => setConfirmDelete(null)}
          >
            Remove {confirmDelete.name}?
          </SheetTitle>
          <div className="rf-muted" style={{ marginBottom: 16 }}>
            It disappears from the draw screen. Every raffle it already produced
            stays in History.
          </div>
          <div className="rf-row rf-gap-8">
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-red rf-flex1"
              onClick={deleteDrawing}
            >
              Remove drawing
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

function DrawingCard({
  drawing,
  open,
  onToggle,
  locationId,
  notify,
  reloadBoot,
  onDelete,
}) {
  const [cfg, setCfg] = useState(null);
  const [bookForm, setBookForm] = useState(null); // { bookId?, colorName, from, to }
  const [seriesOpen, setSeriesOpen] = useState(false);

  const auto = !!drawing.auto;
  const cycle = drawing.cycle;
  // The starting number is only editable while the cycle is empty. Once a
  // customer holds a number it is their record, and renumbering is a new cycle.
  const canSetStart = auto && !(cycle && cycle.issuedCount > 0);

  useEffect(() => {
    if (open) {
      setCfg({
        rollover: drawing.rollover,
        drawsPerEvent: drawing.drawsPerEvent,
        pot: String(drawing.pot || 0),
        potIncrease: String(drawing.potIncrease || 0),
        autoFrom: String((cycle && cycle.autoFrom) || ''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, drawing]);

  const dirty =
    cfg &&
    (cfg.rollover !== drawing.rollover ||
      Number(cfg.drawsPerEvent) !== drawing.drawsPerEvent ||
      Number(cfg.pot) !== drawing.pot ||
      Number(cfg.potIncrease) !== drawing.potIncrease ||
      (canSetStart && Number(cfg.autoFrom) !== (cycle && cycle.autoFrom)));

  const saveCfg = async () => {
    const res = await raffleApi.saveDrawing({
      locationId,
      drawingId: drawing.drawingId,
      name: drawing.name,
      source: drawing.source,
      ...cfg,
    });
    if (!res.success) return notify('error', res.message);
    notify('success', `${drawing.name} saved.`);
    reloadBoot();
  };

  const saveBook = async (patch) => {
    const res = await raffleApi.saveBook({
      locationId,
      drawingId: drawing.drawingId,
      ...patch,
    });
    if (!res.success) return notify('error', res.message);
    setBookForm(null);
    reloadBoot();
  };

  const deleteBook = async (b) => {
    const res = await raffleApi.deleteBook({
      locationId,
      drawingId: drawing.drawingId,
      bookId: b.bookId,
    });
    if (!res.success) return notify('error', res.message);
    reloadBoot();
  };

  return (
    <div className="rf-card rf-nopad" style={{ overflow: 'hidden' }}>
      <button type="button" className="rf-drawing-head" onClick={onToggle}>
        <div className="rf-flex1 rf-minw0 rf-text-left">
          <div className="rf-row rf-gap-8">
            <span className="rf-strong">{drawing.name}</span>
            {auto && (
              <span className="rf-badge-lav">
                <AutoAwesomeIcon style={{ fontSize: 11 }} /> Auto numbers
              </span>
            )}
            {drawing.rollover && (
              <span className="rf-badge-amber">
                <RepeatIcon style={{ fontSize: 11 }} /> Rollover
              </span>
            )}
          </div>
          <div className="rf-xs rf-muted">
            {auto
              ? cycle && cycle.issuedCount > 0
                ? `${cycle.issuedCount} number${
                    cycle.issuedCount === 1 ? '' : 's'
                  } issued this cycle · ${cycle.autoFrom}–${cycle.lastIssued}`
                : `Nothing issued yet · starts at ${
                    (cycle && cycle.autoFrom) || '—'
                  }`
              : `${drawing.books.length} ticket book${
                  drawing.books.length === 1 ? '' : 's'
                }`}
            {drawing.seriesClosed
              ? auto
                ? ' · rollover won, waiting for a new cycle'
                : ' · rollover won, waiting for a new series'
              : ''}
          </div>
        </div>
        <div className="rf-row rf-gap-4">
          {drawing.books.map((b) => (
            <span
              key={b.bookId}
              className="rf-color-dot"
              style={{
                background: TICKET_COLORS.find((c) => c.name === b.colorName)
                  ?.hex,
                opacity: b.active ? 1 : 0.3,
              }}
            />
          ))}
        </div>
        <ChevronRightIcon
          style={{ color: SLATE, transform: open ? 'rotate(90deg)' : 'none' }}
        />
      </button>

      {open && cfg && (
        <div className="rf-divider-top0">
          {auto && (
            <div className="rf-drawing-section">
              <Notice Icon={AutoAwesomeIcon}>
                NuVe issues one number per qualifying visit. The location's
                match rules decide what qualifies, so nothing else has to be set
                up — and nobody hands a ticket out by mistake.
              </Notice>
              <div className="rf-grid3" style={{ marginTop: 12 }}>
                {[
                  ['Issued this cycle', (cycle && cycle.issuedCount) || 0],
                  ['Customers', (cycle && cycle.customerCount) || 0],
                  [
                    'Numbers',
                    cycle && cycle.lastIssued
                      ? `${cycle.autoFrom}–${cycle.lastIssued}`
                      : '—',
                  ],
                ].map(([k, v]) => (
                  <div key={k} className="rf-text-center">
                    <div className="rf-xs rf-muted">{k}</div>
                    <div className="rf-strong">{v}</div>
                  </div>
                ))}
              </div>
              <SectionLabel style={{ marginTop: 14 }}>
                Start numbering at
              </SectionLabel>
              <input
                className="rf-input"
                inputMode="numeric"
                value={cfg.autoFrom}
                disabled={!canSetStart}
                onChange={(e) =>
                  setCfg((c) => ({
                    ...c,
                    autoFrom: digitsOnly(e.target.value),
                  }))
                }
              />
              <div className="rf-xs rf-muted" style={{ marginTop: 6 }}>
                {canSetStart
                  ? 'Where this cycle starts counting. Editable until the first number goes out.'
                  : 'Locked — customers are already holding numbers from this cycle. Start a new cycle to renumber.'}
              </div>
            </div>
          )}
          <div className="rf-drawing-section">
            <div className="rf-row rf-between rf-gap-12">
              <div>
                <div className="rf-strong">Rollover drawing</div>
                <div className="rf-xs rf-muted">
                  The pot carries over when nobody wins
                </div>
              </div>
              <Toggle
                on={cfg.rollover}
                onClick={() => setCfg((c) => ({ ...c, rollover: !c.rollover }))}
              />
            </div>

            {cfg.rollover && (
              <div style={{ marginTop: 12 }}>
                <SectionLabel>
                  How many {auto ? '' : 'ticket '}numbers do you take out per
                  event?
                </SectionLabel>
                <Stepper
                  value={cfg.drawsPerEvent}
                  min={1}
                  max={50}
                  onChange={(v) => setCfg((c) => ({ ...c, drawsPerEvent: v }))}
                />
                <div
                  className="rf-xs rf-muted"
                  style={{ margin: '8px 0 12px' }}
                >
                  After {cfg.drawsPerEvent} number
                  {Number(cfg.drawsPerEvent) === 1 ? '' : 's'} with no winner,
                  the employee gets the option to carry the pot to the next
                  drawing.
                </div>
                <SectionLabel>Current pot</SectionLabel>
                <MoneyInput
                  value={cfg.pot}
                  onChange={(v) => setCfg((c) => ({ ...c, pot: v }))}
                />
                <SectionLabel style={{ marginTop: 12 }}>
                  How much the pot goes up when nobody wins
                </SectionLabel>
                <div className="rf-row rf-gap-8" style={{ marginBottom: 8 }}>
                  {POT_INCREASE_PRESETS.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="rf-seg rf-flex1"
                      data-on={Number(cfg.potIncrease) === v ? '1' : '0'}
                      onClick={() =>
                        setCfg((c) => ({ ...c, potIncrease: String(v) }))
                      }
                    >
                      +${v}
                    </button>
                  ))}
                </div>
                <MoneyInput
                  prefix="+$"
                  value={cfg.potIncrease}
                  onChange={(v) => setCfg((c) => ({ ...c, potIncrease: v }))}
                />
                <div className="rf-xs rf-muted" style={{ marginTop: 8 }}>
                  Only applies when the drawing actually runs. If the drawing is
                  not held, the pot stays the same.
                </div>
              </div>
            )}
            {dirty && (
              <button
                type="button"
                className="rf-btn rf-btn-navy rf-w100"
                style={{ marginTop: 12 }}
                onClick={saveCfg}
              >
                Save {drawing.name}
              </button>
            )}
          </div>

          {!auto &&
            drawing.books.map((b) => (
              <div key={b.bookId} className="rf-book-line">
                <TicketStub number={b.from} color={b.colorName} size="sm" />
                <div className="rf-flex1 rf-minw0">
                  <div className="rf-xs rf-muted">
                    Book {b.from}–{b.to} · {b.to - b.from + 1} tickets
                  </div>
                  <div className="rf-xs rf-strong rf-indigo-text">
                    Given out: {b.givenOut}
                    {b.givenOut > 0 ? ` (up to ${b.lastGivenOut})` : ''}
                  </div>
                </div>
                <Toggle
                  on={b.active}
                  onClick={() => saveBook({ ...b, active: !b.active })}
                />
                <button
                  type="button"
                  className="rf-icon-btn"
                  aria-label="Edit book"
                  onClick={() =>
                    setBookForm({
                      bookId: b.bookId,
                      colorName: b.colorName,
                      from: String(b.from),
                      to: String(b.to),
                      active: b.active,
                    })
                  }
                >
                  <EditIcon style={{ fontSize: 17, color: SLATE }} />
                </button>
                <button
                  type="button"
                  className="rf-icon-btn"
                  aria-label="Delete book"
                  onClick={() => deleteBook(b)}
                >
                  <DeleteOutlineIcon style={{ fontSize: 18, color: SLATE }} />
                </button>
              </div>
            ))}

          {bookForm ? (
            <BookForm
              form={bookForm}
              setForm={setBookForm}
              onSave={() => saveBook(bookForm)}
            />
          ) : (
            <div className="rf-row rf-divider-top0">
              {!auto && (
                <button
                  type="button"
                  className="rf-drawing-action rf-flex1"
                  onClick={() =>
                    setBookForm({
                      colorName: 'Yellow',
                      from: '',
                      to: '',
                      active: true,
                    })
                  }
                >
                  <AddIcon fontSize="small" /> Add ticket book
                </button>
              )}
              {drawing.rollover && (
                <button
                  type="button"
                  className={`rf-drawing-action ${
                    auto ? 'rf-flex1' : 'rf-border-left'
                  }`}
                  onClick={() => setSeriesOpen(true)}
                >
                  New {auto ? 'cycle' : 'series'}
                </button>
              )}
              <button
                type="button"
                className="rf-drawing-action rf-border-left"
                aria-label="Remove drawing"
                onClick={onDelete}
              >
                <DeleteOutlineIcon fontSize="small" style={{ color: SLATE }} />
              </button>
            </div>
          )}
        </div>
      )}

      {seriesOpen && (
        <SeriesSheet
          drawing={drawing}
          defaultFrom={drawing.nextSeriesStart}
          locationId={locationId}
          onClose={() => setSeriesOpen(false)}
          onSaved={() => {
            setSeriesOpen(false);
            notify(
              'success',
              auto ? 'New cycle started.' : 'New ticket series saved.'
            );
            reloadBoot();
          }}
        />
      )}
    </div>
  );
}

function BookForm({ form, setForm, onSave }) {
  const valid = form.from && form.to && Number(form.to) >= Number(form.from);
  return (
    <div className="rf-book-form">
      <SectionLabel>Ticket colour</SectionLabel>
      {['Standard', 'Neon'].map((grp) => (
        <div key={grp} style={{ marginBottom: 12 }}>
          <div className="rf-xs rf-strong-slate" style={{ marginBottom: 8 }}>
            {grp}
          </div>
          <div className="rf-color-grid">
            {TICKET_COLORS.filter((c) => c.group === grp).map((c) => (
              <button
                key={c.name}
                type="button"
                className="rf-color-pick"
                data-on={form.colorName === c.name ? '1' : '0'}
                onClick={() => setForm((f) => ({ ...f, colorName: c.name }))}
              >
                <span
                  className="rf-color-swatch"
                  style={{
                    background: c.hex,
                    boxShadow:
                      grp === 'Neon'
                        ? `0 0 10px ${c.hex}aa`
                        : 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  }}
                />
                <span
                  style={{ color: form.colorName === c.name ? NAVY : SLATE }}
                >
                  {c.name.replace('Neon ', '')}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="rf-card rf-center" style={{ marginBottom: 16 }}>
        <TicketStub
          number={form.from || '1001'}
          color={form.colorName}
          size="lg"
        />
      </div>
      <div className="rf-row rf-gap-8" style={{ marginBottom: 16 }}>
        <div className="rf-flex1">
          <SectionLabel>From</SectionLabel>
          <input
            className="rf-input"
            inputMode="numeric"
            placeholder="1001"
            value={form.from}
            onChange={(e) => {
              const from = digitsOnly(e.target.value);
              setForm((f) => ({ ...f, from }));
            }}
          />
        </div>
        <div className="rf-flex1">
          <SectionLabel>To</SectionLabel>
          <input
            className="rf-input"
            inputMode="numeric"
            placeholder="1500"
            value={form.to}
            onChange={(e) => {
              const to = digitsOnly(e.target.value);
              setForm((f) => ({ ...f, to }));
            }}
          />
        </div>
      </div>
      {form.bookId && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="amber">
            Changing the range keeps the tickets already given out, trimmed to
            the new range.
          </Notice>
        </div>
      )}
      <div className="rf-row rf-gap-8">
        <button
          type="button"
          className="rf-btn rf-btn-outline rf-flex1"
          onClick={() => setForm(null)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-navy rf-flex1"
          disabled={!valid}
          onClick={onSave}
        >
          {form.bookId ? 'Save book' : 'Add book'}
        </button>
      </div>
    </div>
  );
}
